import { CheckIcon, MinusIcon } from "lucide-react";
import type { BillingInterval, Plan } from "@/api/billing";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatCredits, formatLimit, formatPrice } from "./format";

export function IntervalToggle({
	value,
	onChange,
	className,
}: {
	value: BillingInterval;
	onChange: (value: BillingInterval) => void;
	className?: string;
}) {
	return (
		<fieldset
			aria-label="Billing interval"
			className={cn("inline-flex rounded-md border bg-card p-0.5 text-[12px]", className)}
		>
			{(["month", "year"] as const).map((option) => (
				<button
					key={option}
					type="button"
					aria-pressed={value === option}
					onClick={() => onChange(option)}
					className={cn(
						"rounded-[5px] px-2.5 py-1 text-muted-foreground transition-colors duration-75 hover:text-foreground",
						value === option && "bg-accent font-medium text-foreground",
					)}
				>
					{option === "month" ? "Monthly" : "Yearly · 2 months free"}
				</button>
			))}
		</fieldset>
	);
}

export const monthlyEquivalentCents = (plan: Plan, interval: BillingInterval) =>
	interval === "year" ? Math.round(plan.yearlyPriceCents / 12) : plan.monthlyPriceCents;

type PlanGridProps = {
	plans: Plan[];
	currentPlanId: string | undefined;
	currentInterval?: BillingInterval;
	interval: BillingInterval;
	pendingPlanId: string | null;
	disabled?: boolean;
	disabledReason?: string;
	trialEligible?: boolean;
	compact?: boolean;
	onChoose: (plan: Plan) => void;
};

/** Tier cards drawn like spec sheets: numbered tag, price, allowance, limits, one action. */
export function PlanGrid({
	plans,
	currentPlanId,
	currentInterval,
	interval,
	pendingPlanId,
	disabled,
	disabledReason,
	trialEligible,
	compact,
	onChoose,
}: PlanGridProps) {
	const currentIndex = plans.findIndex((plan) => plan.id === currentPlanId);
	return (
		<div
			className={cn(
				"grid gap-3",
				compact ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2 xl:grid-cols-4",
			)}
		>
			{plans.map((plan, index) => {
				const current =
					plan.id === currentPlanId &&
					(plan.monthlyPriceCents === 0 || currentInterval === interval);
				const free = plan.monthlyPriceCents === 0;
				const label = current
					? "Current plan"
					: plan.id === currentPlanId
						? `Switch to ${interval}ly`
						: index > currentIndex
							? trialEligible && plan.trialDays > 0
								? `Start ${plan.trialDays}-day trial`
								: `Upgrade to ${plan.name}`
							: free
								? "Downgrade to Free"
								: `Switch to ${plan.name}`;
				return (
					<article
						key={plan.id}
						className={cn(
							"relative flex flex-col gap-3 rounded-lg border bg-card p-4",
							current && "border-foreground",
						)}
					>
						<div className="flex items-center justify-between">
							<span className="mono-label">Tier {String(index + 1).padStart(2, "0")}</span>
							{current ? (
								<Badge variant="default">Current</Badge>
							) : (
								plan.id === "pro" && <Badge>Popular</Badge>
							)}
						</div>
						<div className="flex flex-col gap-0.5">
							<h3 className="text-[15px] font-medium">{plan.name}</h3>
							{!compact && (
								<p className="text-[12.5px] leading-snug text-muted-foreground">
									{plan.description}
								</p>
							)}
						</div>
						<div className="flex items-baseline gap-1">
							<span className="text-2xl font-medium tracking-tight tabular-nums">
								{formatPrice(monthlyEquivalentCents(plan, interval))}
							</span>
							<span className="text-[12px] text-muted-foreground">
								/ mo{interval === "year" && !free ? " · billed yearly" : ""}
							</span>
						</div>
						<div className="rounded-md border border-dashed px-2.5 py-1.5 font-mono text-[12px]">
							{formatCredits(plan.includedMillicredits)} credits / mo
							{plan.rolloverMillicredits > 0 && (
								<span className="block text-[11px] text-muted-foreground">
									roll over up to {formatCredits(plan.rolloverMillicredits)}
								</span>
							)}
						</div>
						<ul className="flex flex-col gap-1 text-[12.5px]">
							<LimitRow
								label="Seats"
								value={`${plan.seats}${plan.extraSeatPriceCents != null ? ` + ${formatPrice(plan.extraSeatPriceCents)}/seat` : ""}`}
							/>
							<LimitRow label="Projects" value={formatLimit(plan.maxProjects)} />
							<LimitRow label="Social accounts" value={formatLimit(plan.maxSocialAccounts)} />
							<LimitRow label="Flows" value={formatLimit(plan.maxFlows)} />
							{!compact && (
								<>
									<LimitRow
										label="Scheduled flows"
										value={plan.maxSchedules === 0 ? null : formatLimit(plan.maxSchedules)}
									/>
									<LimitRow
										label="Competitors"
										value={plan.maxCompetitors === 0 ? null : formatLimit(plan.maxCompetitors)}
									/>
									<LimitRow label="Ads & boosts" value={plan.features.ads ? true : null} />
									<LimitRow label="Approvals" value={plan.features.approvals ? true : null} />
									<LimitRow
										label="White-label reports"
										value={plan.features.whiteLabelReports ? true : null}
									/>
								</>
							)}
						</ul>
						<Button
							className="mt-auto"
							variant={current ? "outline" : index > currentIndex ? "default" : "outline"}
							size="sm"
							disabled={
								current || disabled || (pendingPlanId !== null && pendingPlanId !== plan.id)
							}
							loading={pendingPlanId === plan.id}
							title={disabled ? disabledReason : undefined}
							onClick={() => onChoose(plan)}
						>
							{label}
						</Button>
					</article>
				);
			})}
		</div>
	);
}

function LimitRow({ label, value }: { label: string; value: string | true | null }) {
	return (
		<li className="flex items-center justify-between gap-2">
			<span className="text-muted-foreground">{label}</span>
			{value === true ? (
				<CheckIcon className="size-3.5" aria-label="Included" />
			) : value === null ? (
				<MinusIcon className="size-3.5 text-muted-foreground/60" aria-label="Not included" />
			) : (
				<span className="font-mono text-[12px] tabular-nums">{value}</span>
			)}
		</li>
	);
}
